﻿/*!
    *
    * Wijmo Library 5.20251.34
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { VueApi, WjComponentBehavior } from '@mescius/wijmo.vue2.base';
import * as wjcChartFinance from '@mescius/wijmo.chart.finance';
var WjFinancialChartBehavior = /** @class */ (function (_super) {
    __extends(WjFinancialChartBehavior, _super);
    function WjFinancialChartBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFinancialChartBehavior.prototype._updateControl = function (property, newValue) {
        switch (property) {
            case 'tooltipContent':
                this.control.tooltip.content = newValue;
                break;
            case 'labelContent':
                this.control.dataLabel.content = newValue;
                break;
            default:
                _super.prototype._updateControl.call(this, property, newValue);
        }
    };
    WjFinancialChartBehavior.tag = 'wj-financial-chart';
    WjFinancialChartBehavior.props = [
        'renderEngine',
        'isDisabled',
        'tabOrder',
        'binding',
        'footer',
        'header',
        'selectionMode',
        'palette',
        'plotMargin',
        'footerStyle',
        'headerStyle',
        'tooltipContent',
        'itemsSource',
        'bindingX',
        'interpolateNulls',
        'legendToggle',
        'symbolSize',
        'options',
        'selection',
        'itemFormatter',
        'labelContent',
        'chartType'
    ];
    WjFinancialChartBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'rendering',
        'rendered',
        'itemsSourceChanging',
        'itemsSourceChanged',
        'seriesVisibilityChanged'
    ];
    WjFinancialChartBehavior.changeEvents = {
        'selectionChanged': ['selection'],
    };
    WjFinancialChartBehavior.classCtor = function () { return wjcChartFinance.FinancialChart; };
    return WjFinancialChartBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.finance.FinancialChart} control.
 *
 * The <b>wj-financial-chart</b> component may contain
 * the following child components:
 * {@link wijmo.vue2.chart.analytics.WjFlexChartTrendLine}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartMovingAverage}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartYFunctionSeries}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartParametricFunctionSeries}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartWaterfall}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartBoxWhisker}
 * , {@link wijmo.vue2.chart.animation.WjFlexChartAnimation}
 * , {@link wijmo.vue2.chart.annotation.WjFlexChartAnnotationLayer}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartFibonacci}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartFibonacciArcs}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartFibonacciFans}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartFibonacciTimeZones}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartAtr}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartCci}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartRsi}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartWilliamsR}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartMacd}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartMacdHistogram}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartStochastic}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartBollingerBands}
 * , {@link wijmo.vue2.chart.finance.analytics.WjFlexChartEnvelopes}
 * , {@link wijmo.vue2.chart.finance.WjFinancialChartSeries}
 * , {@link wijmo.vue2.chart.interaction.WjFlexChartRangeSelector}
 * , {@link wijmo.vue2.chart.interaction.WjFlexChartGestures}
 * , {@link wijmo.vue2.chart.WjFlexChartAxis}
 * , {@link wijmo.vue2.chart.WjFlexChartLegend}
 * , {@link wijmo.vue2.chart.WjFlexChartLineMarker}
 * and {@link wijmo.vue2.chart.WjFlexChartPlotArea}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.finance.FinancialChart} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFinancialChart = WjFinancialChartBehavior.register();
function registerV3WjFinancialChart(app) {
    app.component(WjFinancialChartBehavior.tag, WjFinancialChart);
}
var WjFinancialChartSeriesBehavior = /** @class */ (function (_super) {
    __extends(WjFinancialChartSeriesBehavior, _super);
    function WjFinancialChartSeriesBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFinancialChartSeriesBehavior.tag = 'wj-financial-chart-series';
    WjFinancialChartSeriesBehavior.parentProp = 'series';
    WjFinancialChartSeriesBehavior.siblingId = 'series';
    WjFinancialChartSeriesBehavior.props = [
        'wjProperty',
        'axisX',
        'axisY',
        'binding',
        'bindingX',
        'cssClass',
        'name',
        'altStyle',
        'symbolMarker',
        'symbolSize',
        'symbolStyle',
        'visibility',
        'itemsSource',
        'interpolateNulls',
        'tooltipContent',
        'itemFormatter',
        'chartType'
    ];
    WjFinancialChartSeriesBehavior.events = [
        'initialized',
        'rendering',
        'rendered'
    ];
    WjFinancialChartSeriesBehavior.changeEvents = {
        'chart.seriesVisibilityChanged': ['visibility'],
    };
    WjFinancialChartSeriesBehavior.classCtor = function () { return wjcChartFinance.FinancialSeries; };
    return WjFinancialChartSeriesBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.finance.FinancialSeries} class.
 *
 * The <b>wj-financial-chart-series</b> component should be contained in
 * a {@link wijmo.vue2.chart.finance.WjFinancialChart} component.
 *
 * The <b>wj-financial-chart-series</b> component may contain
 * a {@link wijmo.vue2.chart.WjFlexChartAxis} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.finance.FinancialSeries} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFinancialChartSeries = WjFinancialChartSeriesBehavior.register();
function registerV3WjFinancialChartSeries(app) {
    app.component(WjFinancialChartSeriesBehavior.tag, WjFinancialChartSeries);
}
export function registerChartFinance(app) {
    if (VueApi.isV3Plus) {
        registerV3WjFinancialChart(app);
        registerV3WjFinancialChartSeries(app);
    }
}
